import React from 'react';
import { motion } from 'framer-motion';
import { PlaneIcon, ClockIcon } from 'lucide-react';
import type { Flight } from '../../types';
import { formatNaira } from '../../utils/formatCurrency';
import { formatTime, formatDuration } from '../../utils/dateUtils';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
export interface FlightCardProps {
  flight: Flight;
  onBook?: (flight: Flight) => void;
  canAfford?: boolean;
  walletBalance?: number;
  className?: string;
}
export function FlightCard({
  flight,
  onBook,
  canAfford = true,
  walletBalance,
  className = ''
}: FlightCardProps) {
  return <Card variant="elevated" padding="none" className={className} data-id="element-37">
      <div className="p-4 sm:p-6" data-id="element-38">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-39">
          {/* Airline Info */}
          <div className="flex items-center gap-3 sm:w-32" data-id="element-40">
            <div className="w-10 h-10 bg-surface-100 rounded-lg flex items-center justify-center" data-id="element-41">
              <PlaneIcon className="w-5 h-5 text-surface-600" data-id="element-42" />
            </div>
            <div data-id="element-43">
              <p className="text-body-sm font-medium text-surface-900" data-id="element-44">
                {flight.airline.name}
              </p>
              <p className="text-caption text-surface-500" data-id="element-45">
                {flight.flightNumber}
              </p>
            </div>
          </div>

          {/* Flight Times */}
          <div className="flex-1 flex items-center gap-4" data-id="element-46">
            {/* Departure */}
            <div className="text-center sm:text-left" data-id="element-47">
              <p className="text-heading-lg text-surface-900 tabular-nums" data-id="element-48">
                {formatTime(flight.departureTime)}
              </p>
              <p className="text-body-sm text-surface-600" data-id="element-49">
                {flight.originCity}
              </p>
              <p className="text-caption text-surface-500" data-id="element-50">{flight.origin}</p>
            </div>

            {/* Duration Line */}
            <div className="flex-1 flex flex-col items-center px-2" data-id="element-51">
              <div className="flex items-center gap-1 text-caption text-surface-500 mb-1" data-id="element-52">
                <ClockIcon className="w-3 h-3" data-id="element-53" />
                {formatDuration(flight.duration)}
              </div>
              <div className="w-full flex items-center" data-id="element-54">
                <div className="flex-1 h-px bg-surface-300" data-id="element-55" />
                <PlaneIcon className="w-4 h-4 text-surface-400 mx-2 rotate-90" data-id="element-56" />
                <div className="flex-1 h-px bg-surface-300" data-id="element-57" />
              </div>
              <p className="text-caption text-surface-500 mt-1" data-id="element-58">Direct</p>
            </div>

            {/* Arrival */}
            <div className="text-center sm:text-right" data-id="element-59">
              <p className="text-heading-lg text-surface-900 tabular-nums" data-id="element-60">
                {formatTime(flight.arrivalTime)}
              </p>
              <p className="text-body-sm text-surface-600" data-id="element-61">
                {flight.destinationCity}
              </p>
              <p className="text-caption text-surface-500" data-id="element-62">
                {flight.destination}
              </p>
            </div>
          </div>

          {/* Price & Book */}
          <div className="flex flex-col items-end gap-2 sm:w-40" data-id="element-63">
            <div className="text-right" data-id="element-64">
              <p className="text-heading-lg text-surface-900 tabular-nums" data-id="element-65">
                {formatNaira(flight.price)}
              </p>
              <p className="text-caption text-surface-500 capitalize" data-id="element-66">
                {flight.class}
              </p>
            </div>
            {onBook && <Button variant={canAfford ? 'primary' : 'secondary'} size="sm" onClick={() => onBook(flight)} disabled={!canAfford} data-id="element-67">
                {canAfford ? 'Book with Credit' : 'Insufficient Balance'}
              </Button>}
          </div>
        </div>

        {/* Wallet Balance Hint */}
        {walletBalance !== undefined && !canAfford && <div className="mt-4 pt-4 border-t border-surface-100" data-id="element-68">
            <p className="text-body-sm text-surface-600" data-id="element-69">
              Your wallet balance:{' '}
              <span className="font-medium" data-id="element-70">{formatNaira(walletBalance)}</span>
              <span className="text-surface-500 ml-2" data-id="element-71">
                (Need {formatNaira(flight.price - walletBalance)} more)
              </span>
            </p>
          </div>}
      </div>
    </Card>;
}
// Flight Search Results List
export interface FlightListProps {
  flights: Flight[];
  onBook?: (flight: Flight) => void;
  walletBalance?: number;
  loading?: boolean;
  className?: string;
}
export function FlightList({
  flights,
  onBook,
  walletBalance,
  loading = false,
  className = ''
}: FlightListProps) {
  if (loading) {
    return <div className={`space-y-4 ${className}`} data-id="element-72">
        {Array.from({
        length: 3
      }).map((_, i) => <Card key={i} variant="elevated" padding="md" data-id="element-73">
            <div className="animate-pulse" data-id="element-74">
              <div className="flex items-center gap-4" data-id="element-75">
                <div className="w-10 h-10 bg-surface-200 rounded-lg" data-id="element-76" />
                <div className="flex-1 space-y-2" data-id="element-77">
                  <div className="h-4 bg-surface-200 rounded w-1/4" data-id="element-78" />
                  <div className="h-3 bg-surface-200 rounded w-1/6" data-id="element-79" />
                </div>
                <div className="w-24 h-8 bg-surface-200 rounded" data-id="element-80" />
              </div>
            </div>
          </Card>)}
      </div>;
  }
  if (flights.length === 0) {
    return <div className="text-center py-12" data-id="element-81">
        <div className="w-16 h-16 mx-auto mb-4 bg-surface-100 rounded-full flex items-center justify-center" data-id="element-82">
          <PlaneIcon className="w-8 h-8 text-surface-400" data-id="element-83" />
        </div>
        <p className="text-body-md text-surface-600" data-id="element-84">No flights found</p>
        <p className="text-body-sm text-surface-500 mt-1" data-id="element-85">
          Try adjusting your search criteria
        </p>
      </div>;
  }
  return <div className={`space-y-4 ${className}`} data-id="element-86">
      {flights.map((flight, index) => <motion.div key={flight.id} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: index * 0.1
    }} data-id="element-87">
          <FlightCard flight={flight} onBook={onBook} canAfford={walletBalance !== undefined ? walletBalance >= flight.price : true} walletBalance={walletBalance} data-id="element-88" />
        </motion.div>)}
    </div>;
}